import React, { useMemo } from 'react'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { Formula1Prediction, Formula1Race } from '../types/formula1'
import dayjs from 'dayjs'

/** Prediction with optional race/player populated by API (room predictions with relations) */
interface BestOfPrediction extends Formula1Prediction {
  race?: Formula1Race
  player?: {
    id: string
    _id?: string
    name: string
    avatar?: string | null
  }
}

interface BestOfModalFormula1Props {
  isOpen: boolean
  roomId: string
  onClose: () => void
  isLoading?: boolean
  predictions?: BestOfPrediction[]
}

const BestOfModalFormula1: React.FC<BestOfModalFormula1Props> = ({
  isOpen,
  onClose,
  isLoading = false,
  predictions = [],
}) => {
  const { t } = useTranslation()

  // Only show predictions that scored points (completed race with correct guesses)
  const bestPredictions = useMemo(() => {
    return predictions.filter((p) => (p.totalPoints ?? 0) > 0)
  }, [predictions])

  // Group by race (race name, date), then sort by race date (most recent first)
  const byRace = useMemo(() => {
    const map = new Map<string, BestOfPrediction[]>()
    bestPredictions.forEach((p) => {
      const race = p.race
      const key = race?.id ?? p.raceId ?? 'unknown'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(p)
    })
    return Array.from(map.entries())
      .map(([raceId, preds]) => {
        const race = preds[0]?.race
        const raceDate = race?.raceDate
        return {
          raceId,
          raceName: race?.city ?? 'Race',
          raceDate: raceDate ? dayjs(raceDate).format('MMM D, YYYY') : '',
          raceDay: race?.day ?? 0,
          sortDate: raceDate ? new Date(raceDate).getTime() : 0,
          predictions: preds.sort((a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0)),
        }
      })
      .sort((a, b) => b.sortDate - a.sortDate)
  }, [bestPredictions])

  const formatPredictedTop10 = (pred: BestOfPrediction) => {
    const pos = pred.predictedPositions
    if (!pos || typeof pos !== 'object') return ''
    const entries = Object.entries(pos)
      .map(([driver, position]) => ({ driver, position: Number(position) }))
      .filter((e) => e.position >= 1 && e.position <= 10)
      .sort((a, b) => a.position - b.position)
    if (entries.length === 0) return ''
    return entries.map((e) => `${e.position}. ${e.driver}`).join(', ')
  }

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full sm:max-w-4xl sm:mx-4 glass-strong rounded-2xl p-3 sm:p-6 lg:p-8 max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ minHeight: isLoading ? '280px' : 'auto' }}
      >
        <div className="flex justify-between items-center mb-2 sm:mb-6 gap-2">
          <div className="flex items-center gap-1.5 sm:gap-4 min-w-0">
            <span className="text-warning text-base sm:text-2xl lg:text-3xl flex-shrink-0">🏆</span>
            <h2 className="text-nav sm:text-display-2 font-display font-bold text-foreground truncate">
              {t('bestOfF1.title')}
            </h2>
            <span className="text-warning text-base sm:text-2xl lg:text-3xl flex-shrink-0">🏆</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 sm:p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all flex-shrink-0"
            aria-label={t('bestOf.close')}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-1 px-1 flex flex-col">
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-12 min-h-[200px]">
              <div className="text-4xl sm:text-6xl mb-2 sm:mb-4 animate-spin inline-block" aria-hidden>🏎️</div>
              <h3 className="text-nav sm:text-display-2 font-semibold text-foreground mb-1 sm:mb-2">
                {t('bestOfF1.loading')}
              </h3>
              <p className="text-small sm:text-body-lg text-muted-foreground">{t('bestOfF1.loadingDesc')}</p>
            </div>
          ) : bestPredictions.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-12 min-h-[200px]">
              <div className="text-4xl sm:text-6xl mb-2 sm:mb-4">🏎️</div>
              <h3 className="text-nav sm:text-display-2 font-semibold text-foreground mb-1 sm:mb-2">
                {t('bestOfF1.empty')}
              </h3>
              <p className="text-small sm:text-body-lg text-muted-foreground">{t('bestOfF1.emptyDesc')}</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-6">
              {byRace.map(({ raceId, raceName, raceDate, raceDay, predictions: racePreds }) => (
                <div key={raceId} className="space-y-1.5 sm:space-y-4">
                  <div className="glass-card py-2 px-2.5 sm:p-4 rounded-lg sm:rounded-xl">
                    <h3 className="text-small sm:text-display-2 font-bold text-warning text-center">
                      {t('bestOfF1.raceLabel', { day: String(raceDay), name: raceName, date: raceDate })}
                    </h3>
                  </div>

                  {racePreds.map((pred) => {
                    const player = pred.player
                    const name = player?.name ?? 'Unknown'
                    const pts = pred.totalPoints ?? 0
                    const predictedText = formatPredictedTop10(pred)
                    return (
                      <div
                        key={pred.id ?? pred.raceId + pred.playerId}
                        className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 py-2 px-2.5 sm:p-4 glass-card rounded-lg sm:rounded-xl"
                      >
                        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                          <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-foreground font-bold text-[10px] sm:text-body-lg">
                              {name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-small sm:text-display-2 font-bold text-foreground truncate">{name}</h4>
                            {predictedText && (
                              <p className="text-muted-foreground text-[10px] sm:text-sm mt-0.5 line-clamp-2 sm:line-clamp-3">
                                {predictedText}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-end sm:justify-center flex-shrink-0">
                          <span className="font-bold text-foreground text-[11px] sm:text-base">
                            {formatPoints(pts, t)}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 pt-3 sm:mt-6 sm:pt-6 border-t border-muted/30 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl text-small sm:text-nav font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            {t('bestOf.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BestOfModalFormula1
